import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSupportMeeting } from '../features/meetings/hooks';
import { useUser } from '../features/auth/useUser';
import { formatDate } from '../utils/formatters';
import '../styles/buttons.css';
import './SupportMeetingPage.css';

const TITLE_ICON = '/video_call.svg';

/** billing_period_start (YYYY-MM-DD) の翌サイクル開始日 = 次回リセット日を返す */
function nextResetDate(billingPeriodStart: string): string {
  const [y, m, d] = billingPeriodStart.split('-').map(Number);
  if (!y || !m || !d) return '—';
  // 翌月の同日。ただし翌月に存在しない日（月末アンカー）はその月の最終日へ
  // クランプする（バックエンドの relativedelta と同じ挙動。JS の Date は
  // クランプせずオーバーフローする＝2026-01-31 → 03-03 になってしまうため明示的に丸める）。
  // m は 1-indexed なので、翌月は Date の月インデックス m に対応する。
  const lastDayOfNextMonth = new Date(y, m + 1, 0).getDate();
  const day = Math.min(d, lastDayOfNextMonth);
  // Date は文字列化せずそのまま渡す（toISOString だと UTC 変換で 1 日ズレるため）。
  const next = new Date(y, m, day);
  return formatDate(next);
}

/**
 * 予約ページをモーダル内の iframe で表示する。
 * HubSpot の MeetingsEmbedCode.js は独自ラッパーを差し込み、外周に白い面が
 * 出るため使わない。生 iframe を全面表示してモーダル側でトリミングする。
 */
function HubSpotMeetingModal({ url, onClose }: { url: string; onClose: () => void }) {
  // Escape で閉じ、開いている間は背面スクロールを止める。
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const src = url.includes('?') ? `${url}&embed=true` : `${url}?embed=true`;

  return createPortal(
    <div className="support-meeting-overlay" onClick={onClose}>
      <div className="support-meeting-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="support-meeting-modal-close"
          onClick={onClose}
          aria-label="閉じる"
        >
          ×
        </button>
        <div className="support-meeting-modal-body">
          <iframe
            className="support-meeting-embed"
            src={src}
            title="サポートミーティング予約ページ"
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            allow="camera; microphone; fullscreen"
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="page admin-page support-meeting-page">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">
            <img src={TITLE_ICON} alt="" className="page-title-icon" />
            サポートミーティング
          </h1>
          <p className="page-description">担当者との打ち合わせを予約し、利用状況を確認できます</p>
        </div>
      </div>
      {children}
    </div>
  );
}

export default function SupportMeetingPage() {
  const { isAuthenticated } = useUser();
  const { data, isLoading, isError } = useSupportMeeting(isAuthenticated);
  const [bookingOpen, setBookingOpen] = useState(false);

  if (isLoading) {
    return (
      <PageShell>
        <div className="admin-loading">読み込み中...</div>
      </PageShell>
    );
  }

  if (isError || !data) {
    return (
      <PageShell>
        <div className="admin-error">データの取得に失敗しました</div>
      </PageShell>
    );
  }

  // 対象外プラン（メニューからは到達しないが直接アクセス時のフォールバック）
  if (!data.eligible || !data.quota) {
    return (
      <PageShell>
        <div className="support-empty">
          ご利用中のプランではサポートミーティングをご利用いただけません。
        </div>
      </PageShell>
    );
  }

  const { booking_url, quota, history } = data;

  return (
    <PageShell>
      <div className="support-section-row">
        {/* 予約 */}
        <section className="support-section">
          <h2 className="support-section-title">
            <img src="/messages_people_person_bubble_circle_1.svg" alt="" className="support-section-title-icon" />
            ミーティングを予約
          </h2>
          <div className="support-panel">
            <p className="support-panel-lead">
              担当者とのサポートミーティング予約はこちら
            </p>
            {booking_url ? (
              <button
                type="button"
                onClick={() => setBookingOpen(true)}
                className="btn-modal btn-modal-accent support-book-btn"
              >
                予約ページを開く
              </button>
            ) : (
              <button type="button" disabled className="btn-modal support-book-btn" title="予約ページは現在準備中です">
                予約ページ準備中
              </button>
            )}
            {quota.credits_per_session > 0 && (
              <p className="support-book-note">
                {quota.credits_per_session.toLocaleString()}ポイント／回
              </p>
            )}
          </div>
        </section>

        {/* 現契約期間の無償サポート枠 */}
        <section className="support-section">
          <h2 className="support-section-title">
            <img src="/time_clock_circle.svg" alt="" className="support-section-title-icon" />
            現契約期間の無償サポート枠
          </h2>
          <div className="support-panel">
            <div className="support-quota">
              <div className="support-quota-figure">
                <span className="support-quota-remaining">{quota.free_sessions_remaining}</span>
                <span className="support-quota-unit">
                  回 残り（無償 月{quota.free_sessions_quota}回）
                </span>
              </div>
              <p className="support-quota-meta">
                次回リセット: {nextResetDate(quota.billing_period_start)}
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* 実施履歴 */}
      <section className="support-section">
        <h2 className="support-section-title">
          <img src="/play_list_1.svg" alt="" className="support-section-title-icon" />
          実施履歴
        </h2>
        {history.length > 0 ? (
          <table className="support-history-table">
            <thead>
              <tr>
                <th className="col-date">実施日</th>
                <th>内容</th>
                <th className="col-type">区分</th>
              </tr>
            </thead>
            <tbody>
              {history.map((item) => (
                <tr key={item.id}>
                  <td className="col-date">{formatDate(item.conducted_at)}</td>
                  <td>{item.title || '—'}</td>
                  <td className="col-type">
                    <span className={`support-badge ${item.is_free_session ? 'support-badge--free' : 'support-badge--paid'}`}>
                      {item.is_free_session ? '無料枠' : '追加'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="support-empty">実施履歴はまだありません</div>
        )}
      </section>

      {booking_url && bookingOpen && (
        <HubSpotMeetingModal url={booking_url} onClose={() => setBookingOpen(false)} />
      )}
    </PageShell>
  );
}
